import type { Moment } from 'moment-timezone';

import { moment, TimeFormat } from '@feathr/hooks';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/**
 * Sets `target`'s time of day to `source`'s hour and minute, zeroing seconds and milliseconds.
 * Mailrail send times are whole minutes, so sub-minute components are always zeroed to keep
 * schedule dates serializing and comparing consistently. Mutates and returns `target`.
 */
export function copyRoundedTimeOfDay(target: Moment, source: Moment): Moment {
  return target.set({
    hour: source.hour(),
    minute: source.minute(),
    second: 0,
    millisecond: 0,
  });
}

/**
 * react-datepicker works in the browser's timezone. Returns a browser-local Date with `m`'s
 * wall-clock time, so the picker shows profile-timezone times (1:30 PM EDT shows as "1:30 PM").
 */
export function toPickerDate(m: Moment): Date {
  return new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second());
}

/**
 * Date-only DatePickers read `selected`'s UTC calendar date. Returns UTC midnight of `m`'s
 * calendar date, so a late-day profile time doesn't show as the next day.
 */
export function toDateOnlyPickerDate(m: Moment): Date {
  return new Date(Date.UTC(m.year(), m.month(), m.date()));
}

/**
 * Inverse of `toPickerDate` for `onDateStrChange`: reads the picked wall-clock time in `timezone`
 * and returns it as UTC. Without a timezone, returns `timestamp` unchanged.
 */
export function fromPickerTimestamp(
  timestamp: string | undefined,
  timezone: string | undefined,
): string | undefined {
  if (!timestamp || !timezone) {
    return timestamp;
  }
  const picked = moment.utc(timestamp).toDate();
  return moment
    .tz(
      [
        picked.getFullYear(),
        picked.getMonth(),
        picked.getDate(),
        picked.getHours(),
        picked.getMinutes(),
        picked.getSeconds(),
      ],
      timezone,
    )
    .utc()
    .format(TimeFormat.isoDateTime);
}

/**
 * Default `date_start` for scheduled Mailrail sends: now + 15 minutes, rounded up to the next
 * 5-minute boundary, formatted as an ISO date-time string. Rounds the full epoch timestamp so
 * seconds and milliseconds always round upward — rounding the minute field alone would truncate
 * them backwards past the 15-minute floor whenever the minute already sits on a boundary.
 */
export function getDefaultStartTimestamp(): string {
  const earliestStartMs = moment.utc().add(15, 'minutes').valueOf();
  const roundedStartMs = Math.ceil(earliestStartMs / FIVE_MINUTES_MS) * FIVE_MINUTES_MS;
  return moment.utc(roundedStartMs).format(TimeFormat.isoDateTime);
}
